import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { isDeviceOnline, getDeviceLastActivityTime } from 'common/utils/deviceTwinUtils';
import classnames from 'classnames';
import { IconLoader } from 'common/IconLoader';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { LinearProgress } from 'components/shared/Progress';
import BundleOverviewControllerActionIconsContainer from '../BundleOverviewControllerActionIconsContainer/BundleOverviewControllerActionIconsContainer';
import LTEDetailsModal from 'components/features/LTEDetailsModal/LTEDetailsModal';
import { getBaselineStatus,getBaselineOperationalProgress, BASELINE_UPDATE_STATUS_LABELS } from 'components/features/ProvisionModal/utils';
import { OperationalStates, OperationalStateMessages, getOperationalState, getOperationalStateProgress } from './utils';

const BundleOverviewControllerGa2Tab = ({
  id,
  device,
  deviceType,
  isServiceUnit,
  activeIndex,
  donorSwVersion,
  scrollTo,
  sendSelectedDevice,
  setActiveIndex,
  setTargetDevice,
  handleLockUnlockAction,
  setIsLockUnlockModalOpen,
  handleWiFiAction,
  ga2ApiVersion
}) => {
  const timeoutRef = useRef(null);
  const baselineTimeoutRef = useRef(null);
  const [isLTEDetailsModalOpen, setIsLTEDetailsModalOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [isBaselinePending, setIsBaselinePending] = useState(false);
  const triangleDiv = (
    <div className="BundleOverviewController_ArrowDown" id="BundleOverviewController_ArrowDown"></div>
  );
  const swVersion = donorSwVersion;
  const isOnline = isDeviceOnline(device);
  const lastActivityTime = getDeviceLastActivityTime(device);
  const closeLTEDetailsModal = useCallback(() => {
    setIsLTEDetailsModalOpen(false);
  }, []);

  const rfPath = device?.type?.slice(2);
  const serviceInfo = device?.rfPath?.['service-info'];
  const rawOperationalState = device?.rfPath?.['service-info']?.['operational-state'] || 'Unknown';
  const operationalState = getOperationalState(serviceInfo);
  const operationalStateProgress = getOperationalStateProgress(serviceInfo);
  const isSwitching = [OperationalStates.DISABLING, OperationalStates.ENABLING].includes(operationalState);
  // Get baseline status and progress
  const swBaseline = getBaselineStatus(device?.['baselining-status']?.['update-status-code']) || 'Unknown';
  
  // Check if the API version is v3 or higher
  // We only care about the first valid response, so if ga2ApiVersion is defined and v3+, set isV3OrHigher to true
  const isV3OrHigher = ga2ApiVersion && ga2ApiVersion !== 'v1' && ga2ApiVersion !== 'v2'
  
  // Store the API version check result in a ref to maintain it across renders
  const apiVersionCheckedRef = useRef(false);
  const isV3OrHigherRef = useRef(false);
  
  // Only update the ref if we haven't checked a valid version yet or if we find a v3+ version
  if (!apiVersionCheckedRef.current && ga2ApiVersion) {
    apiVersionCheckedRef.current = true;
    isV3OrHigherRef.current = isV3OrHigher;
  } else if (isV3OrHigher) {
    // If we find a v3+ version, always update the ref to true
    isV3OrHigherRef.current = true;
  }
  const isBaseLineInProg = isV3OrHigherRef.current
    ? getBaselineOperationalProgress(device?.['baselining-status'], true) 
    : getBaselineOperationalProgress(device?.['baselining-status']);
  // Check if the displayed status is "In Progress" or if the code is "baseliningInProg"
  const isBaselining = swBaseline === BASELINE_UPDATE_STATUS_LABELS.baseliningInProg || 
                       device?.['baselining-status']?.['update-status-code'] === 'baseliningInProg';
  
  const deviceId = device?.deviceId || device?.id;

  // Handle operational state transitions
  useEffect(() => {
    if (!isPending && isSwitching) {
      setIsPending(true);
    } else if (isPending && !isSwitching && !timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        setIsPending(false);
        timeoutRef.current = null;
      }, 5_000);
    }
  }, [isPending, isSwitching]);

  // Handle baselining state transitions
  useEffect(() => {
    if (!isBaselinePending && isBaselining) {
      setIsBaselinePending(true);
    } else if (isBaselinePending && !isBaselining && !baselineTimeoutRef.current) {
      // Only show success/error for 5 seconds after completion
      baselineTimeoutRef.current = setTimeout(() => {
        setIsBaselinePending(false);
        baselineTimeoutRef.current = null;
      },2000); 
    }
  }, [isBaselinePending, isBaselining]);

  // Clean up timeouts
  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(baselineTimeoutRef.current);
    };
  }, []);

  return (
    <div
      id={`BundleOverviewControllerTab_${deviceType}_${id}`}
      key={id}
      className={classnames('BundleOverviewControllerTab', {
        BundleOverviewControllerTabFocus: activeIndex === deviceId
      })}
      onClick={() => {
        scrollTo && scrollTo(id);
        sendSelectedDevice && sendSelectedDevice(device);
        setActiveIndex && setActiveIndex(deviceId);
        setTargetDevice && setTargetDevice(device);
      }}
    >
      {isPending && (
        <div className="BundleOverviewControllerGa2Progress">
          <div className="BundleOverviewControllerGa2ProgressContainer">
            <div className="BundleOverviewControllerGa2ProgressStatus">
              {OperationalStateMessages[operationalState] ?? operationalState}
            </div>
            {isSwitching ? (
              <div className="BundleOverviewControllerGa2ProgressBar">
                <LinearProgress value={operationalStateProgress} height={5} />
              </div>
            ) : (
              <img
                className="BundleOverviewControllerGa2ProgressIcon"
                alt=""
                src={operationalState === OperationalStates.ERROR ? IconLoader.icon_error : IconLoader.icon_success}
              />
            )}
          </div>
          {activeIndex === deviceId && <div className="BundleOverviewControllerGa2ProgressArrow" />}
        </div>
      )}
      {(isBaselinePending || isBaselining) && isV3OrHigherRef.current  && (
        <div className="BundleOverviewControllerGa2Progress">
          <div className="BundleOverviewControllerGa2ProgressContainer">
            <div className="BundleOverviewControllerGa2ProgressStatus">
              {swBaseline}
            </div>
            {!isBaselining ? (
              <div className="BundleOverviewControllerGa2ProgressBar">
                <LinearProgress value={isBaseLineInProg} height={5} />
              </div>
            ) : (
              <img
                className="BundleOverviewControllerGa2ProgressIcon"
                alt=""
                src={swBaseline === BASELINE_UPDATE_STATUS_LABELS.baselineError ? IconLoader.icon_error : IconLoader.icon_success}
              />
            )}
          </div>
          {activeIndex === deviceId && <div className="BundleOverviewControllerGa2ProgressArrow" />}
        </div>
      )}
      {isLTEDetailsModalOpen && <LTEDetailsModal device={device} isGa2={true} onClose={closeLTEDetailsModal} />}
      {activeIndex === deviceId && triangleDiv}
      <div className="BundleOverviewControllerTabDeviceLabel">
        <SvgAF
          img={
            deviceType === 'echo'
              ? IconLoader.icon_echo
              : isServiceUnit
              ? IconLoader.icon_service
              : IconLoader.icon_donor
          }
        />
        <div className="BundleOverviewControllerTabDeviceLabelId" data-tip={deviceId}>
          {deviceId}
        </div>
      </div>
      {!isServiceUnit ? (
        <div className="BundleOverviewControllerTabConnectionContainer">
          <div className="BundleOverviewControllerTabConnectionBandType"></div>
          {device ? (
            <div
              className={classnames({
                BundleOverviewControllerTabConnectionOnline: isOnline,
                BundleOverviewControllerTabConnectionOffline: !isOnline
              })}
              data-tip={!isOnline ? `Offline since ${lastActivityTime}` : 'Online'}
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsLTEDetailsModalOpen(true);
              }}
            >
              {isOnline ? 'ONLINE' : 'OFFLINE'}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="BundleOverviewControllerTabRfPathContainer">
          <div className="BundleOverviewControllerTabRfPathHeader">Sector</div>
          <div className="BundleOverviewControllerTabRfPathValue">{rfPath}</div>
        </div>
      )}
      <div className="BundleOverViewControllerTabBottomContainer">
        {!isServiceUnit && (
          <div className="BundleOverviewControllerTabSwVersionContainer">
            <div className="BundleOverviewControllerTabSwVersionLabel">System SW Release</div>
            <div className="BundleOverviewControllerTabSwVersionValue" data-tip={swVersion}>
              {swVersion}
            </div>
          </div>
        )}

        {isServiceUnit && (
          <div className="BundleOverviewControllerTabSwVersionContainer">
            <div className="BundleOverviewControllerTabSwVersionLabel">SW Baseline</div>
            <div className="BundleOverviewControllerTabSwVersionValue" data-tip={swBaseline}>
              {swBaseline}
            </div>
          </div>
        )}

        {isServiceUnit && (
          <div className="BundleOverviewControllerTabSwVersionContainer">
            <div className="BundleOverviewControllerTabSwVersionLabel">Ops. State</div>
            <div className="BundleOverviewControllerTabSwVersionValue" data-tip={rawOperationalState}>
              {rawOperationalState}
            </div>
          </div>
        )}
        {!isServiceUnit && (
          <BundleOverviewControllerActionIconsContainer
            device={device}
            isGa2={true}
            deviceType={deviceType}
            setTargetDevice={setTargetDevice}
            handleLockUnlockAction={handleLockUnlockAction}
            setIsLockUnlockModalOpen={setIsLockUnlockModalOpen}
            handleWiFiAction={handleWiFiAction}
          />
        )}
      </div>
    </div>
  );
};

export default memo(BundleOverviewControllerGa2Tab);
